import { useApolloMutation } from "frontend/utils/apolloApi/useApolloMutation.js";

// gql
import {
    SET_EDITORIAL_USE_ONLY_APPROVAL_ON_UPLOADS,
    SET_EDITORIAL_USE_ONLY_ON_UPLOADS
} from "frontend/components/fort/mod/photos/gql/mutations";

// utils
import { useApiUploadUpdate } from "frontend/components/fort/mod/photos/hooks/useApiUploadUpdate.js";

// hooks
import { useFetchNonModMutationSynch } from "../../queries/useFetchNonModMutationSynch";

/**
 * resolveMissingEuoApprove: optimistically make editorialUseOnlyApproved === editorialUseOnly
 *
 * We have two related boolean props: editorialUseOnly and editorialUseOnlyApproved.
 * Public API action addRemoveEUOFlag toggles editorialUseOnly but does not return editorialUseOnlyApproved,
 * while the private MOD API approveDenyEUOFlag returns both.
 * This creates a temporary inconsistency in Redux where editorialUseOnlyApproved is stale,
 * resulting in a UI flash until a separate MOD fetch reconciles the correct value.
 *
 * @param {object} ssResp Api Response missing editorialUseOnlyApproved
 */
const resolveMissingEuoApprove = ssResp => {
    let optSsResp = { ...ssResp };

    const photoUploads = optSsResp?.data?.payload?.photoUploads;

    if (photoUploads.length > 0) {
        const newPhotoUploads = photoUploads.map(pu => {
            const { photoUpload } = pu;

            if (
                typeof photoUpload?.editorialUseOnly === "boolean" &&
                !("editorialUseOnlyApproved" in photoUpload)
            ) {
                return {
                    ...pu,
                    photoUpload: {
                        ...photoUpload,
                        editorialUseOnlyApproved: photoUpload.editorialUseOnly
                    }
                };
            }

            return pu; // untouched if no update needed
        });
        optSsResp.data.payload.photoUploads = newPhotoUploads;
    }

    return optSsResp;
};

export const useModifyEditorialUseOnlyFlag = () => {
    const { fetchNonModMutationSynch } = useFetchNonModMutationSynch();
    const { apiUploadUpdate } = useApiUploadUpdate();

    const updateAndSynch = async ssResp => {
        apiUploadUpdate(ssResp);

        // Cannot rely on above query to synch all MOD props
        await fetchNonModMutationSynch();
    };

    // Approve Deny: Mod Api Call - has editorialUseOnlyApproved in response
    const { apolloMutate: approveDenyMutate } = useApolloMutation({
        actionDesc: "Uploads: Flags: Editorial Use Only: Approve Deny",
        gql: SET_EDITORIAL_USE_ONLY_APPROVAL_ON_UPLOADS
    });

    const approveDenyEUOFlag = async ({ photoUploadIds, approve = true }) => {
        const { ssResp } = await approveDenyMutate({
            variables: {
                photoUploadIds,
                value: approve
            }
        });
        await updateAndSynch(ssResp);
    };

    // Add Remove: Public Api Call - does not have editorialUseOnlyApproved in response
    const { apolloMutate: addRemoveMutate } = useApolloMutation({
        actionDesc: "Uploads: Flags: Editorial Use Only: Add Remove",
        gql: SET_EDITORIAL_USE_ONLY_ON_UPLOADS
    });

    const addRemoveEUOFlag = async ({ photoUploadIds, add = true }) => {
        const { ssResp } = await addRemoveMutate({
            variables: {
                photoUploadIds,
                value: add
            }
        });
        let optSsResp = resolveMissingEuoApprove(ssResp);
        await updateAndSynch(optSsResp);
    };

    return {
        approveDenyEUOFlag,
        addRemoveEUOFlag
    };
};
